import {
    onScopeDispose,
    shallowRef,
    toValue,
    watch,
    type MaybeRefOrGetter,
    type ShallowRef,
} from 'vue'
import { STOP_IN_MODAL_TAG } from '~/pageTools/snapshot'

const POLL_MS = 300
/** The shadow roots searched for a modal when the document has none. */
const MAX_SHADOW_ROOTS = 50
const WIDGET_TAGS = new Set(['EMBEDDED-CHAT-WIDGET', 'FLOATING-CHAT-WIDGET'])

const modalsIn = (root: Document | ShadowRoot) => [
    ...root.querySelectorAll<HTMLDialogElement>('dialog:modal'),
]

/** The open shadow roots under `root`, but the widget's: its own dialog is no page modal. */
const shadowRootsIn = (root: Document | ShadowRoot) =>
    [...root.querySelectorAll('*')]
        .filter((element) => !WIDGET_TAGS.has(element.tagName))
        .map((element) => element.shadowRoot)
        .filter((shadow): shadow is ShadowRoot => !!shadow)

/** The focused element, down through the open shadow roots. */
const deepActiveElements = (): Element[] => {
    const chain: Element[] = []
    let active = document.activeElement
    while (active) {
        chain.push(active)
        active = active.shadowRoot?.activeElement ?? null
    }
    return chain
}

/**
 * The modal dialog the page has open, the one on top when there are several:
 * the innermost one that holds the focus, which opening a modal moves into
 * it, or the last one found. It makes the rest of the page inert, the widget
 * included: the chat, its cards and the control bar take no click while it is
 * open. A modal inside the page's own shadow roots counts too, looked for only
 * when the document has none. `skip` leaves out a modal about to close.
 */
const openPageModal = (
    skip?: (modal: HTMLDialogElement) => boolean,
): HTMLDialogElement | null => {
    // Left out before looking further: a modal of a shadow root under the
    // document's one that closes is found at once
    const kept = (root: Document | ShadowRoot) =>
        modalsIn(root).filter((modal) => !skip?.(modal))
    let modals: HTMLDialogElement[]
    try {
        modals = kept(document)
        const queue = modals.length === 0 ? shadowRootsIn(document) : []
        for (
            let searched = 0;
            modals.length === 0 &&
            queue.length > 0 &&
            searched < MAX_SHADOW_ROOTS;
            searched++
        ) {
            const root = queue.shift()!
            modals = kept(root)
            queue.push(...shadowRootsIn(root))
        }
    } catch {
        // A browser without `:modal` has no way to tell: none
        return null
    }
    const focused = deepActiveElements()
    // The innermost: a modal opened inside another holds the focus in both
    return (
        [...modals]
            .reverse()
            .find((modal) =>
                focused.some((element) => modal.contains(element)),
            ) ??
        modals[modals.length - 1] ??
        null
    )
}

/**
 * The page's open modal dialog, looked for while `watching` is true (a card
 * waits, or the agent acts on the page) and `null` the rest of the time. A
 * page does not say when it opens one, so it is polled.
 */
export function usePageModal(
    watching: MaybeRefOrGetter<boolean>,
): ShallowRef<HTMLDialogElement | null> {
    const modal = shallowRef<HTMLDialogElement | null>(null)
    let timer: ReturnType<typeof setInterval> | undefined
    const stop = () => {
        clearInterval(timer)
        timer = undefined
        modal.value = null
    }
    watch(
        () => toValue(watching),
        (on) => {
            stop()
            if (!on) {
                return
            }
            modal.value = openPageModal()
            timer = setInterval(() => {
                const current = openPageModal()
                if (current !== modal.value) {
                    modal.value = current
                }
            }, POLL_MS)
        },
        { immediate: true },
    )
    onScopeDispose(stop)
    return modal
}

/** The colors of the control bar the widget shows, or a dark bar when it is not found. */
const barColors = (): { ink: string; paper: string } => {
    for (const widget of document.querySelectorAll(
        'embedded-chat-widget, floating-chat-widget',
    )) {
        const bar = widget.shadowRoot?.querySelector('.pk-page-control-bar')
        if (bar) {
            const { backgroundColor, color } = getComputedStyle(bar)
            return { ink: backgroundColor, paper: color }
        }
    }
    return { ink: '#111', paper: '#fff' }
}

/** Where the stop sits by default: under the beam along the frame's edge, as the control bar. */
const STOP_TOP = 48
const STOP_GAP = 8

/**
 * Keeps the stop off the dialog it sits in (spec
 * `page-tools-improvements.md`, M7): where the control bar is when the
 * dialog starts below it, else just above the dialog, else just below it.
 * With no room outside, over the dialog's top, where its title is, rather
 * than over its buttons.
 */
const placeStop = (bar: HTMLElement, dialog: HTMLElement) => {
    const rect = dialog.getBoundingClientRect()
    const height = bar.offsetHeight
    const below = window.innerHeight - rect.bottom
    let top: number | undefined = STOP_TOP
    let bottom: number | undefined
    if (rect.top < STOP_TOP + height + STOP_GAP) {
        if (rect.top >= height + 2 * STOP_GAP) {
            top = rect.top - height - STOP_GAP
        } else if (below >= height + 2 * STOP_GAP) {
            top = undefined
            bottom = below - height - STOP_GAP
        }
    }
    bar.style.top = top === undefined ? 'auto' : `${top}px`
    bar.style.bottom = bottom === undefined ? 'auto' : `${bottom}px`
    // It comes in from the side it sits on, and leaves the same way
    bar.style.setProperty(
        '--stop-from',
        `${top === undefined ? 1 : -1} * ${STOP_SLIDE}px`,
    )
}

/** How far the stop slides in and out, in pixels. */
const STOP_SLIDE = 16

const reducedMotion = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

/** Takes the stop away, sliding it out the way it came unless motion is reduced. */
const leave = (host: HTMLElement) => {
    const bar = host.shadowRoot?.querySelector('div')
    if (!bar || typeof bar.animate !== 'function' || reducedMotion()) {
        host.remove()
        return
    }
    const from =
        bar.style.getPropertyValue('--stop-from') || `-1 * ${STOP_SLIDE}px`
    bar.animate(
        [
            { opacity: 1, translate: '0 0', scale: '1' },
            { opacity: 0, translate: `0 calc(${from})`, scale: '0.96' },
        ],
        { duration: 200, easing: 'ease-in', fill: 'forwards' },
    ).finished.then(
        () => host.remove(),
        () => host.remove(),
    )
}

/**
 * The stop of the control bar inside the page's modal dialog, while the agent
 * acts on the page: the bar itself is inert behind that dialog, and the user
 * must be able to stop the agent at any moment. Only the dialog's own content
 * takes clicks, so the button goes in it, in a shadow root the page's styles
 * do not reach. The page snapshot skips it, as it skips the widget.
 */
export function usePageModalStop(
    modal: ShallowRef<HTMLDialogElement | null>,
    active: MaybeRefOrGetter<boolean>,
    options: {
        text: () => string
        stopLabel: () => string
        onStop: () => void
    },
) {
    let host: HTMLElement | undefined
    let placing: ReturnType<typeof setInterval> | undefined
    const remove = () => {
        clearInterval(placing)
        placing = undefined
        if (host) {
            leave(host)
        }
        host = undefined
    }
    watch(
        [modal, () => toValue(active)],
        ([dialog, on]) => {
            remove()
            if (!dialog || !on) {
                return
            }
            host = document.createElement(STOP_IN_MODAL_TAG)
            const root = host.attachShadow({ mode: 'open' })
            const style = document.createElement('style')
            // The control bar's look, written out: the widget's stylesheet
            // lives in its own shadow root. Its colors come from the bar the
            // widget shows, which follow the theme
            const { ink, paper } = barColors()
            style.textContent = `
                :host { all: initial; }
                div {
                    position: fixed; top: ${STOP_TOP}px; left: 50%; transform: translateX(-50%);
                    z-index: 2147483647; display: flex; align-items: center; gap: 12px;
                    max-width: min(384px, calc(100vw - 32px)); padding: 8px 8px 8px 16px;
                    border-radius: 9999px; background: ${ink}; color: ${paper};
                    font: 500 14px/1.4 system-ui, sans-serif;
                    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 20%);
                    animation: stop-in 0.35s cubic-bezier(0.32, 0.72, 0, 1) both;
                }
                @keyframes stop-in {
                    from { opacity: 0; translate: 0 calc(var(--stop-from, -1 * ${STOP_SLIDE}px)); scale: 0.96; }
                }
                @media (prefers-reduced-motion: reduce) {
                    div { animation: none; }
                }
                span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
                button {
                    flex-shrink: 0; border: 0; border-radius: 9999px; padding: 6px 14px;
                    background: ${paper}; color: ${ink}; font: inherit; cursor: pointer;
                }`
            const bar = document.createElement('div')
            bar.setAttribute('role', 'status')
            const label = document.createElement('span')
            label.textContent = options.text()
            const button = document.createElement('button')
            button.type = 'button'
            button.textContent = options.stopLabel()
            button.addEventListener('click', () => options.onStop())
            bar.append(label, button)
            root.append(style, bar)
            dialog.append(host)
            // Measured again on every round: the same dialog may grow or
            // move while the agent works in it
            const place = () => placeStop(bar, dialog)
            place()
            placing = setInterval(place, POLL_MS)
        },
        { immediate: true },
    )
    onScopeDispose(remove)
}

/** How often the dock looks again by itself, for a modal no event announces: one in a shadow root. */
const DOCK_POLL_MS = 1000

/**
 * The frames a dialog just opened gets before the dock trusts that no
 * transition runs: a framework starts the entering one a frame or two after
 * `showModal()` (Vue's `Transition` two).
 */
const SETTLE_FRAMES = 3

/** The longest wait for a dialog's entrance: an animation paused or endless in time never ends. */
const SETTLE_MAX_MS = 1000

/** The attribute of the element that keeps the widget's place in the page while it is in a dialog. */
export const DOCK_PLACEHOLDER_ATTRIBUTE = 'data-agent-studio-placeholder'

/** Elements the widget never goes into: a click in the chat would be theirs. */
const INTERACTIVE =
    'a, button, input, label, select, summary, textarea, [contenteditable]'

/** The widget's own layers, which Esc still closes. */
const OWN_LAYERS = 'dialog[open], [popover]:popover-open'

/**
 * The widget's look in a dialog, fixed to the window; `place` adds its box.
 * Longhands only: a shorthand put back empty would take with it the page's
 * own `right` or `margin-left`.
 */
const PINNED: [string, string][] = [
    ['position', 'fixed'],
    ['right', 'auto'],
    ['bottom', 'auto'],
    ['margin-top', '0'],
    ['margin-right', '0'],
    ['margin-bottom', '0'],
    ['margin-left', '0'],
    ['box-sizing', 'border-box'],
    ['max-width', 'none'],
    ['max-height', 'none'],
    ['z-index', '2147483646'],
]
const BOX = ['left', 'top', 'width', 'height']

type Movable = ParentNode & {
    moveBefore?: (node: Node, child: Node | null) => void
}

/** A browser that moves an element without taking it out of the page. */
const canMove = () =>
    typeof (Element.prototype as Movable).moveBefore === 'function'

/** The running animations and transitions of `elements` that come to an end: an endless one is no change of state. */
const settling = (...elements: Element[]) =>
    elements
        .flatMap((element) => element.getAnimations?.() ?? [])
        .filter(
            (animation) =>
                animation.playState === 'running' &&
                Number.isFinite(
                    animation.effect?.getComputedTiming().iterations ??
                        Infinity,
                ),
        )

/**
 * An element on its way out: an animation or a transition, played once
 * forward or once in reverse, takes it to no opacity. A dialog that pulses or
 * fades in is not leaving.
 */
const fadingOut = (element: Element) =>
    settling(element).some((animation) => {
        const effect = animation.effect as KeyframeEffect | null
        if (!effect?.getKeyframes || animation.playbackRate <= 0) {
            return false
        }
        const { iterations, direction } = effect.getComputedTiming()
        if (
            iterations !== 1 ||
            (direction !== 'normal' && direction !== 'reverse')
        ) {
            return false
        }
        const frames = effect.getKeyframes()
        const end =
            direction === 'reverse' ? frames[0] : frames[frames.length - 1]
        return !!end && 'opacity' in end && Number(end.opacity) === 0
    })

/** A dialog that fades out, or whose panel does. */
const dialogLeaving = (dialog: HTMLDialogElement) =>
    fadingOut(dialog) || [...dialog.children].some(fadingOut)

/** At least 95% of `outer` both ways: a backdrop rather than a panel. */
const covers = (rect: DOMRect, outer: { width: number; height: number }) =>
    rect.width >= outer.width * 0.95 && rect.height >= outer.height * 0.95

/**
 * Where the widget goes in a dialog. A dialog that covers the window draws its
 * own backdrop, and its panel is the largest child it shows that does not
 * cover it too (a backdrop of its own): the widget goes there, so a page that
 * closes the dialog on a click outside its panel is less likely to take a
 * click in the chat for one. Any other dialog is its own panel.
 */
const dockTarget = (dialog: HTMLDialogElement): Element => {
    const box = dialog.getBoundingClientRect()
    if (
        !covers(box, { width: window.innerWidth, height: window.innerHeight })
    ) {
        return dialog
    }
    const area = (element: Element) => {
        const rect = element.getBoundingClientRect()
        return covers(rect, box) ? 0 : rect.width * rect.height
    }
    const panel = [...dialog.children]
        .filter((child) => area(child) > 0)
        .sort((a, b) => area(b) - area(a))[0]
    return panel && !panel.matches(INTERACTIVE) ? panel : dialog
}

/** In the page, not in an inert part of it, and with a size. */
const shown = (element: Element) =>
    element.isConnected &&
    !element.closest('[inert]') &&
    element.getBoundingClientRect().width > 0

/**
 * Moves the widget with `moveBefore`, which keeps it in the page and its state
 * with it: an answer being written, the audio of a call, an iframe. The scroll
 * and the focus are put back where a browser drops them (Firefox resets the
 * scroll). False when the browser refuses the move.
 */
const moveWidget = (
    parent: ParentNode,
    host: HTMLElement,
    before: Node | null,
): boolean => {
    const root = host.shadowRoot
    const scrolled = [...(root?.querySelectorAll('*') ?? [])]
        .filter((element) => element.scrollTop > 0)
        .map((element) => [element, element.scrollTop] as const)
    const focused = root?.activeElement as HTMLElement | null | undefined
    const target: Movable = parent
    try {
        target.moveBefore!(host, before)
    } catch {
        // Not movable as is: a parent out of the page, another document
        return false
    }
    for (const [element, top] of scrolled) {
        element.scrollTop = top
    }
    if (focused && root?.activeElement !== focused) {
        focused.focus({ preventScroll: true })
    }
    return true
}

/**
 * The chat inside the page's modal dialog (spec `page-tools-improvements.md`,
 * M11). A modal makes everything outside it inert, the widget too: while
 * `enabled`, the widget moves into the dialog on top, fixed where it was on
 * screen, over a placeholder that keeps its place in the page, and goes back
 * when the dialog closes, fades out or leaves the page, or when its place goes
 * out of sight (a panel the user closed). A browser without `moveBefore` keeps
 * the widget where it is: a plain move would take it out of the page, and its
 * state with it.
 *
 * Returns whether the widget is in the dialog and takes clicks there, which is
 * checked on the first frame and again on every change the dock sees: until
 * then, and wherever it does not, the card in the top layer and the stop in
 * the page's dialog stay on.
 */
export function usePageModalDock(
    host: HTMLElement | null,
    enabled: MaybeRefOrGetter<boolean>,
): ShallowRef<boolean> {
    const usable = shallowRef(false)
    if (!host || !canMove()) {
        return usable
    }
    let on = false
    // The dialog that holds the widget, the element of it the widget went
    // into, and the place it keeps in the page
    let dialog: HTMLDialogElement | undefined
    let holder: Element | undefined
    let placeholder: HTMLElement | undefined
    let home: { parent: ParentNode; next: Node | null } | undefined
    let unpin: (() => void) | undefined
    let shift = { x: 0, y: 0 }
    let observer: MutationObserver | undefined
    let frame = 0
    let polling: ReturnType<typeof setInterval> | undefined
    // A dialog that says it is closing, still modal while it says so
    let closing: HTMLDialogElement | undefined
    // A dialog still coming in, waited for (a wait of its own, which a later
    // one replaces), and the ones that came in
    let waiting: { dialog: HTMLDialogElement } | undefined
    const settled = new Set<HTMLDialogElement>()
    // Dialogs where the widget would not take clicks, or that its owner took
    // it out of, until they close
    const refused = new Set<HTMLDialogElement>()

    const pin = () => {
        const saved = [...PINNED.map(([name]) => name), ...BOX].map(
            (name) =>
                [
                    name,
                    host.style.getPropertyValue(name),
                    host.style.getPropertyPriority(name),
                ] as const,
        )
        for (const [name, value] of PINNED) {
            host.style.setProperty(name, value, 'important')
        }
        return () => {
            for (const [name, value, priority] of saved) {
                if (value) {
                    host.style.setProperty(name, value, priority)
                } else {
                    host.style.removeProperty(name)
                }
            }
        }
    }

    const set = (name: string, px: number) => {
        const value = `${px}px`
        if (host.style.getPropertyValue(name) !== value) {
            host.style.setProperty(name, value, 'important')
        }
    }

    /**
     * Over the placeholder. An ancestor with a transform, a filter or a
     * containment holds a fixed element instead of the window: the shift it
     * adds is measured and taken off.
     */
    const place = () => {
        const want = placeholder!.getBoundingClientRect()
        set('width', want.width)
        set('height', want.height)
        for (let round = 0; round < 2; round++) {
            set('left', want.left - shift.x)
            set('top', want.top - shift.y)
            const got = host.getBoundingClientRect()
            const x = got.left - want.left
            const y = got.top - want.top
            if (Math.abs(x) < 0.5 && Math.abs(y) < 0.5) {
                return
            }
            shift = { x: shift.x + x, y: shift.y + y }
        }
    }

    /**
     * The widget takes clicks in its place: its box is the placeholder's, no
     * ancestor hides it (a panel faded or made invisible), and the point at
     * its center is its own. Asked of the widget's shadow root, which sees its
     * own elements even inside a shadow root of the page, and past its own
     * layers: a card of the widget over the chat says nothing of the chat.
     */
    const fits = () => {
        const want = placeholder!.getBoundingClientRect()
        const got = host.getBoundingClientRect()
        const same = (
            [
                [got.left, want.left],
                [got.top, want.top],
                [got.width, want.width],
                [got.height, want.height],
            ] as const
        ).every(([a, b]) => Math.abs(a - b) < 1)
        if (
            !same ||
            host.checkVisibility?.({
                opacityProperty: true,
                visibilityProperty: true,
            }) === false
        ) {
            return false
        }
        const root = host.shadowRoot
        const own = (element: Element) => !!root?.contains(element)
        const hit = (root ?? document)
            .elementsFromPoint(
                got.left + got.width / 2,
                got.top + got.height / 2,
            )
            .find((element) => !(own(element) && element.closest(OWN_LAYERS)))
        return !!hit && (host.contains(hit) || own(hit))
    }

    const escape = (event: KeyboardEvent) => {
        if (event.key !== 'Escape') {
            return
        }
        // Esc in the chat is no request to close the page's dialog, but it
        // still closes a dialog or a popover of the widget itself
        const path = event.composedPath()
        const own = path
            .slice(0, path.indexOf(host))
            .some((node) => node instanceof Element && node.matches(OWN_LAYERS))
        if (!own) {
            event.preventDefault()
        }
    }

    /**
     * Out of the dialog. A widget still where it went, in the dialog or in a
     * part of it the page took out (the dialog itself, a panel it replaced),
     * goes back to its placeholder, even one out of the page: its owner took
     * the place away (an unmount, a cache), and the widget goes with it as it
     * would have. A widget its owner took out of the dialog stays where the
     * owner put it, and that dialog is not tried again.
     */
    const release = () => {
        observer?.disconnect()
        observer = undefined
        cancelAnimationFrame(frame)
        host.removeEventListener('keydown', escape)
        const held = !!holder?.contains(host) || !!dialog?.contains(host)
        if (dialog && !held && host.isConnected) {
            refused.add(dialog)
        }
        if (held) {
            const spot = placeholder?.parentNode
                ? { parent: placeholder.parentNode, next: placeholder }
                : home
            if (spot) {
                const before =
                    spot.next?.parentNode === spot.parent ? spot.next : null
                const moved =
                    spot.parent.isConnected &&
                    host.isConnected &&
                    moveWidget(spot.parent, host, before)
                if (!moved) {
                    spot.parent.insertBefore(host, before)
                }
            }
        }
        placeholder?.remove()
        unpin?.()
        dialog = undefined
        holder = undefined
        placeholder = undefined
        home = undefined
        unpin = undefined
        usable.value = false
    }

    /**
     * The widget does not take clicks in the dialog: back to its place. A
     * dialog still moving gets another try once it stops, any other is left
     * alone until it closes.
     */
    const fail = () => {
        const held = dialog!
        const moving = settling(held, ...held.children).length > 0
        if (moving) {
            settled.delete(held)
        } else {
            refused.add(held)
        }
        release()
        if (moving) {
            reconcile()
        }
    }

    const follow = () => {
        if (!dialog || !placeholder) {
            return
        }
        if (!shown(placeholder)) {
            const held = dialog
            release()
            // A place that shows nothing while the widget did: the page's
            // layout does not hold the placeholder, and docking again would
            // go round every frame
            if (shown(host)) {
                refused.add(held)
            }
            reconcile()
            return
        }
        place()
        // Checked on the first frame in the dialog: only a widget that takes
        // clicks there turns the card and the stop off
        if (!usable.value) {
            if (!fits()) {
                fail()
                return
            }
            usable.value = true
        }
        frame = requestAnimationFrame(follow)
    }

    const dock = (target: HTMLDialogElement) => {
        const into = dockTarget(target)
        const { height } = host.getBoundingClientRect()
        const spot = document.createElement('div')
        // The host's classes and inline style keep its place in the layout
        spot.className = host.className
        spot.style.cssText = host.style.cssText
        if (!spot.style.height) {
            spot.style.minHeight = `${height}px`
        }
        spot.setAttribute('aria-hidden', 'true')
        spot.setAttribute(DOCK_PLACEHOLDER_ATTRIBUTE, '')
        home = { parent: host.parentNode!, next: host.nextSibling }
        home.parent.insertBefore(spot, host)
        placeholder = spot
        unpin = pin()
        if (!moveWidget(into, host, null)) {
            refused.add(target)
            release()
            return
        }
        dialog = target
        holder = into
        shift = { x: 0, y: 0 }
        place()
        host.addEventListener('keydown', escape)
        observer = new MutationObserver(reconcile)
        observer.observe(target, {
            attributes: true,
            attributeFilter: ['open', 'class', 'style'],
        })
        // A panel that fades out or hides says so on itself
        if (into !== target) {
            observer.observe(into, {
                attributes: true,
                attributeFilter: ['class', 'style', 'hidden'],
            })
        }
        observer.observe(document, { childList: true, subtree: true })
        frame = requestAnimationFrame(follow)
    }

    /** Where the widget belongs now: it is moved there. */
    const reconcile = () => {
        if (!on) {
            return
        }
        // A dialog that closed starts over when it opens again, one in a
        // shadow root too, whose `toggle` never reaches the document
        const gone = (modal: HTMLDialogElement) =>
            !modal.open || !modal.isConnected
        for (const set of [refused, settled]) {
            for (const modal of set) {
                if (gone(modal)) {
                    set.delete(modal)
                }
            }
        }
        if (waiting && gone(waiting.dialog)) {
            waiting = undefined
        }
        const top = openPageModal((modal) => modal === closing)
        const target =
            top &&
            !refused.has(top) &&
            top.getBoundingClientRect().width > 0 &&
            !dialogLeaving(top)
                ? top
                : null
        if (dialog) {
            if (
                dialog === target &&
                !!holder?.contains(host) &&
                dialog.contains(holder) &&
                !!placeholder &&
                shown(placeholder)
            ) {
                // Checked again on every change: a panel that hid, something
                // laid over the chat
                if (usable.value && !fits()) {
                    fail()
                }
                return
            }
            release()
        }
        // Refused by the release itself: the owner moved the widget away
        if (!target || refused.has(target) || !shown(host)) {
            return
        }
        if (!settled.has(target)) {
            settle(target)
            return
        }
        dock(target)
    }

    /**
     * Waits for a dialog to come in: a few frames, then the transitions that
     * started, a second at most. Docked before, the chat would fade in with
     * the dialog, or be clipped by a panel that slides in.
     */
    const settle = (target: HTMLDialogElement) => {
        if (waiting?.dialog === target) {
            return
        }
        const wait = { dialog: target }
        waiting = wait
        let frames = SETTLE_FRAMES
        const next = () => {
            if (waiting !== wait) {
                return
            }
            if (--frames > 0) {
                requestAnimationFrame(next)
                return
            }
            const coming = settling(target, ...target.children)
            void Promise.race([
                Promise.allSettled(
                    coming.map((animation) => animation.finished),
                ),
                new Promise((resolve) => setTimeout(resolve, SETTLE_MAX_MS)),
            ]).then(() => {
                if (waiting === wait) {
                    waiting = undefined
                    settled.add(target)
                    reconcile()
                }
            })
        }
        requestAnimationFrame(next)
    }

    const beforeToggle = (event: Event) => {
        // Out while the dialog still shows what it holds, and into the one
        // below it
        if (
            event.target === dialog &&
            (event as ToggleEvent).newState === 'closed'
        ) {
            closing = dialog
            reconcile()
            closing = undefined
        }
    }

    const toggle = (event: Event) => {
        if (event.target instanceof HTMLDialogElement) {
            reconcile()
        }
    }

    const start = () => {
        on = true
        // Dialogs of the document announce themselves; the poll finds the
        // ones in shadow roots, whose events stay inside
        document.addEventListener('beforetoggle', beforeToggle, true)
        document.addEventListener('toggle', toggle, true)
        polling = setInterval(reconcile, DOCK_POLL_MS)
        reconcile()
    }

    const stop = () => {
        on = false
        document.removeEventListener('beforetoggle', beforeToggle, true)
        document.removeEventListener('toggle', toggle, true)
        clearInterval(polling)
        polling = undefined
        waiting = undefined
        if (dialog) {
            release()
        }
        refused.clear()
        settled.clear()
    }

    watch(
        () => toValue(enabled),
        (enable) => {
            stop()
            if (enable) {
                start()
            }
        },
        { immediate: true },
    )
    onScopeDispose(stop)
    return usable
}
